import * as React from 'react';
import { css } from '@emotion/css';
import classNames from 'classnames';
import { I13nDiv } from 'react-i13n';
import { theme } from '@branchjs/neo-components';
import Title from './Title';
import type { TitleProps } from '../../types/titleTypes';
type Item<T extends string> = {
  /**
   * id should be a string without spaces, used for data-testid and input id
   */
  id: T;

  /**
   * label is what will appear to the right of the radio input circle, can be a string or an element
   */
  label: string | React.ReactElement<any>;

  /**
   * optional className to be applied to the outermost div of that item
   * The specific className on the RadioItem over-rides any 'allItemsClassName'
   * passed to the RadioGroup component
   */
  className?: string;

  /**
   * disables the individual radio items (cannot be clicked)
   */
  disabled?: boolean;

  /**
   * className to be applied to the label
   */
  labelClassName?: string;
};
type RadioGroupProps<T extends string> = {
  items: Array<Item<T>>;

  /**
   * id for the group as a whole
   */
  groupId: string;

  /**
   * optional className to be applied to every item in the radio group.
   * This can be over-ridden for specific items by passing a className
   * as part of the object in props.items array.
   */
  allItemsClassName?: string;

  /**
   * id for the item that is currently checked, passed in as prop to parent element RadioGroup
   */
  checkedId?: T;

  /**
   * optional className to be applied to the outermost div. Individual classNames
   * can additionally be added as properties to any item object to apply to just that item.
   */
  className?: string;

  /**
   * disable radio items from being changed (disables the whole group)
   */
  disabled?: boolean;

  /**
   * optional title displayed above the radio group as a whole
   */
  groupLabel?: string;

  /**
   * callback passed down from parent to be called whenever an item is clicked
   */
  onClick?: (id: T) => void;

  /**
   * optional i13nModel - default properties will already be set, so you only
   * need to add if you're adding params most likely
   */
  i13nModel?: Record<string, any>;

  /**
   * optional children are displayed underneath to checked radio item.
   */
  children?: React.ReactNode;
} & TitleProps;
// SUBCOMPONENT OF RADIOGROUP
type RadioItemProps<T extends string> = {
  groupId: string;
  item: Item<T>;
  checkedId?: T;
  className?: string;
  disabled?: boolean;
  i13nModel?: Record<string, any>;
  labelClassName?: string;
  onClick?: (id: T) => void;
};

class RadioItem<T extends string> extends React.PureComponent<RadioItemProps<T>> {
  render() {
    const id = `${this.props.groupId}__${this.props.item.id}`;
    const className = this.props.item.className || this.props.className || '';
    const i13nLabel = typeof this.props.item.label === 'string' ? `Radios - ${this.props.item.label}` : 'Radio Group';
    const i13nModel = { ...this.props.i13nModel, action: 'click', label: i13nLabel };
    return (
      <I13nDiv
        onClick={this.props.onClick ? this.props.onClick.bind(this, this.props.item.id) : null}
        className={classNames('radio-group--item', className, {
          disabled: this.props.disabled,
        })}
        i13nModel={i13nModel}
        bindClickEvent={true}
        data-testid={id}
      >
        <label>
          <input
            checked={this.props.checkedId === this.props.item.id}
            type="radio"
            name={this.props.groupId}
            id={id}
            readOnly={true}
          />
          <div
            className={classNames('radio-group--item-label', {
              disabled: this.props.disabled,
            })}
          >
            <span className={`radio-group--item-label-text ${this.props.item.labelClassName || ''}`}>
              {this.props.item.label}
            </span>
          </div>
        </label>
      </I13nDiv>
    );
  }
}

const styles = {
  radioItemCompoundContainerClass: css({
    border: `1px solid ${theme.colors.gray[200]}`,
    borderRadius: '5px',
    marginTop: '12px',
  }),
  radioItemLabelContainerClass: css({
    padding: '16px 16px 0px 16px',
    cursor: 'pointer',
  }),
  childrenContainerClass: css({
    borderTop: `1px solid ${theme.colors.gray[200]}`,
    padding: '20px',
  }),
};

class RadioGroup<T extends string> extends React.PureComponent<RadioGroupProps<T>> {
  static defaultProps = {
    groupId: 'radio-group',
    className: '',
  };

  _getRadioItems = () => {
    if (this.props.children) {
      return this.props.items.map((item) => (
        <div
          className={styles.radioItemCompoundContainerClass}
          key={item.id}
          data-testid={`radio-item__compound-container-${item.id}`}
        >
          <div className={styles.radioItemLabelContainerClass}>
            <RadioItem
              groupId={this.props.groupId}
              item={item}
              checkedId={this.props.checkedId}
              className={this.props.allItemsClassName}
              i13nModel={this.props.i13nModel}
              labelClassName={item.labelClassName}
              onClick={this.props.onClick}
              disabled={item.disabled || this.props.disabled}
            />
          </div>
          {this.props.checkedId === item.id && (
            <div key="radio-group__children" className={styles.childrenContainerClass}>
              {this.props.children}
            </div>
          )}
        </div>
      ));
    }

    return this.props.items.map((item) => (
      <RadioItem
        key={item.id}
        groupId={this.props.groupId}
        item={item}
        checkedId={this.props.checkedId}
        className={this.props.allItemsClassName}
        i13nModel={this.props.i13nModel}
        labelClassName={item.labelClassName}
        onClick={this.props.onClick}
        disabled={item.disabled || this.props.disabled}
      />
    ));
  };

  render() {
    return (
      <div
        className={classNames('radio-group', this.props.className)}
        data-testid={`radio-group-${this.props.groupId || ''}`}
      >
        <Title
          description={this.props.description}
          label={this.props.groupLabel}
          labelClassName={this.props.labelClassName}
          tooltip={this.props.tooltip}
        />
        {this._getRadioItems()}
      </div>
    );
  }
}

export default RadioGroup;
